import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';

function AdminTariffsPage() {
  const [tariffs, setTariffs] = useState([]);
  const [squads, setSquads] = useState([]);
  const [tariffLevels, setTariffLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [squadsLoading, setSquadsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token } = useAuth();
  
  const [name, setName] = useState('');
  const [days, setDays] = useState(30);
  const [priceUAH, setPriceUAH] = useState(0);
  const [priceRUB, setPriceRUB] = useState(0);
  const [priceUSD, setPriceUSD] = useState(0);
  const [selectedSquadIds, setSelectedSquadIds] = useState([]);  // Массив выбранных сквадов
  const [trafficLimitGB, setTrafficLimitGB] = useState(0);
  const [hwidDeviceLimit, setHwidDeviceLimit] = useState(0);
  const [selectedTier, setSelectedTier] = useState('');
  const [badge, setBadge] = useState('');
  const [bonusDays, setBonusDays] = useState(0);
  
  // Состояние для редактирования
  const [editingTariff, setEditingTariff] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDays, setEditDays] = useState(30);
  const [editPriceUAH, setEditPriceUAH] = useState(0);
  const [editPriceRUB, setEditPriceRUB] = useState(0);
  const [editPriceUSD, setEditPriceUSD] = useState(0);
  const [editSelectedSquadIds, setEditSelectedSquadIds] = useState([]);  // Массив выбранных сквадов
  const [editTrafficLimitGB, setEditTrafficLimitGB] = useState(0);
  const [editHwidDeviceLimit, setEditHwidDeviceLimit] = useState(0);
  const [editSelectedTier, setEditSelectedTier] = useState('');
  const [editBadge, setEditBadge] = useState('');
  const [editBonusDays, setEditBonusDays] = useState(0);
  
  // Загрузка тарифов
  useEffect(() => {
    const fetchTariffs = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('/api/admin/tariffs', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) throw new Error('Не удалось загрузить тарифы');
        const data = await response.json();
        setTariffs(data);
      } catch (e) { setError(e.message); } 
      finally { setLoading(false); }
    };
    
    if (token) {
        fetchTariffs();
    }
  }, [token]);

  // Загрузка сквадов
  useEffect(() => {
    const fetchSquads = async () => {
      setSquadsLoading(true);
      try {
        const response = await fetch('/api/admin/squads', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          // Backend уже обработал структуру и вернул массив
          const squadsList = Array.isArray(data) ? data : [];
          setSquads(squadsList);
        }
      } catch (e) {
        console.error('Ошибка загрузки сквадов:', e);
      } finally {
        setSquadsLoading(false);
      }
    };
    
    if (token) {
        fetchSquads();
    }
  }, [token]);

  // Загрузка уровней тарифов
  useEffect(() => {
    const fetchTariffLevels = async () => {
      try {
        const response = await fetch('/api/admin/tariff-levels', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setTariffLevels(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        console.error('Ошибка загрузки уровней тарифов:', e);
      }
    };
    
    if (token) {
      fetchTariffLevels();
    }
  }, [token]);

  const getTierLabel = (tierCode) => {
    if (!tierCode) return '';
    const found = tariffLevels.find(l => l.code === tierCode);
    return found ? `${found.name} (${found.code})` : tierCode;
  };

  // --- Создание тарифа ---
  const handleCreateTariff = async (e) => {
    e.preventDefault();
    try {
      // Конвертируем гигабайты в байты (1 GB = 1073741824 bytes)
      const trafficLimitBytes = trafficLimitGB > 0 ? Math.round(trafficLimitGB * 1073741824) : 0;
      
      const response = await fetch('/api/admin/tariffs', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: name,
          duration_days: days,
          price_uah: priceUAH,
          price_rub: priceRUB,
          price_usd: priceUSD,
          squad_ids: selectedSquadIds.length > 0 ? selectedSquadIds : null,
          traffic_limit_bytes: trafficLimitBytes,
          hwid_device_limit: hwidDeviceLimit > 0 ? hwidDeviceLimit : null,
          tier: selectedTier || null,
          badge: badge || null,
          bonus_days: bonusDays > 0 ? bonusDays : null
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Ошибка создания');
      
      setName(''); setDays(30); setPriceUAH(0); setPriceRUB(0); setPriceUSD(0); setSelectedSquadIds([]); setTrafficLimitGB(0); setHwidDeviceLimit(0); setSelectedTier(''); setBadge(''); setBonusDays(0);
      // ❗️ ИЗМЕНЕНИЕ: Обновляем список вручную
      setTariffs(prev => [...prev, data.response || data]);
      
    } catch (e) { alert(e.message); }
  };
  
  // --- Открытие формы редактирования ---
  const handleEditTariff = (tariff) => {
    setEditingTariff(tariff);
    setEditName(tariff.name || '');
    setEditDays(tariff.duration_days || 30);
    setEditPriceUAH(tariff.price_uah || 0);
    setEditPriceRUB(tariff.price_rub || 0);
    setEditPriceUSD(tariff.price_usd || 0);
    // Загружаем squad_ids из тарифа (может быть JSON строка или массив)
    let squadIds = [];
    if (tariff.squad_ids) {
      try {
        squadIds = typeof tariff.squad_ids === 'string' ? JSON.parse(tariff.squad_ids) : tariff.squad_ids;
      } catch (e) {
        squadIds = [];
      }
    } else if (tariff.squad_id) {
      // Обратная совместимость со старым полем squad_id
      squadIds = [tariff.squad_id];
    }
    setEditSelectedSquadIds(Array.isArray(squadIds) ? squadIds : []);
    setEditTrafficLimitGB(tariff.traffic_limit_bytes ? (tariff.traffic_limit_bytes / 1073741824) : 0);
    setEditHwidDeviceLimit(tariff.hwid_device_limit || 0);
    setEditSelectedTier(tariff.tier || '');
    setEditBadge(tariff.badge || '');
    setEditBonusDays(tariff.bonus_days || 0);
  };
  
  // --- Закрытие формы редактирования ---
  const handleCloseEdit = () => {
    setEditingTariff(null);
    setEditName('');
    setEditDays(30);
    setEditPriceUAH(0);
    setEditPriceRUB(0);
    setEditPriceUSD(0);
    setEditSelectedSquadIds([]);
    setEditTrafficLimitGB(0);
    setEditHwidDeviceLimit(0);
    setEditSelectedTier('');
    setEditBadge('');
    setEditBonusDays(0);
  };
  
  // --- Обновление тарифа ---
  const handleUpdateTariff = async (e) => {
    e.preventDefault();
    if (!editingTariff) return;
    
    try {
      const trafficLimitBytes = editTrafficLimitGB > 0 ? Math.round(editTrafficLimitGB * 1073741824) : 0;
      
      const response = await fetch(`/api/admin/tariffs/${editingTariff.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editName,
          duration_days: editDays,
          price_uah: editPriceUAH,
          price_rub: editPriceRUB,
          price_usd: editPriceUSD,
          squad_ids: editSelectedSquadIds.length > 0 ? editSelectedSquadIds : null,
          traffic_limit_bytes: trafficLimitBytes,
          hwid_device_limit: editHwidDeviceLimit > 0 ? editHwidDeviceLimit : null,
          tier: editSelectedTier || null,
          badge: editBadge || null,
          bonus_days: editBonusDays > 0 ? editBonusDays : null
        })
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Ошибка обновления');
      
      // Обновляем список тарифов
      setTariffs(prev => prev.map(t => 
        t.id === editingTariff.id 
          ? { ...t, ...data.response, name: editName, duration_days: editDays, price_uah: editPriceUAH, price_rub: editPriceRUB, price_usd: editPriceUSD, squad_ids: editSelectedSquadIds.length > 0 ? editSelectedSquadIds : null, traffic_limit_bytes: trafficLimitBytes, tier: editSelectedTier || null, badge: editBadge || null, bonus_days: editBonusDays > 0 ? editBonusDays : 0 }
          : t
      ));
      
      handleCloseEdit();
    } catch (e) { 
      alert(e.message); 
    }
  };
  
  // --- Удаление тарифа ---
  const handleDeleteTariff = async (tariffId) => {
    if (!window.confirm("Удалить этот тариф?")) return;
    try {
       const response = await fetch(`/api/admin/tariffs/${tariffId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Ошибка удаления');
      // ❗️ ИЗМЕНЕНИЕ: Обновляем список вручную
      setTariffs(prev => prev.filter(t => t.id !== tariffId));
    } catch (e) { alert(e.message); }
  };

  return (
    <main className="admin-page-content">
      {/* Секция 1: Форма создания */}
      <section>
        <h2>Создать новый тариф</h2>
        <form className="tariff-form" onSubmit={handleCreateTariff}>
          {/* ... (весь JSX формы без изменений) ... */}
          <div className="form-row">
            <div className="form-group">
              <label>Название (н.п. "1 Месяц")</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Длительность (в днях)</label>
              <input type="number" value={days} onChange={e => setDays(e.target.value)} required />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Цена (UAH)</label>
              <input type="number" value={priceUAH} onChange={e => setPriceUAH(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Цена (RUB)</label>
              <input type="number" value={priceRUB} onChange={e => setPriceRUB(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Цена (USD)</label>
              <input type="number" value={priceUSD} onChange={e => setPriceUSD(e.target.value)} required />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group" style={{ flex: '1 1 100%' }}>
              <label>Сквады (можно выбрать несколько)</label>
              <div style={{ 
                border: '1px solid var(--glass-border)', 
                borderRadius: '8px', 
                padding: '12px', 
                maxHeight: '250px', 
                overflowY: 'auto',
                backgroundColor: 'var(--surface-muted)',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px'
              }}>
                {squadsLoading ? (
                  <small style={{ width: '100%' }}>Загрузка сквадов...</small>
                ) : squads.length === 0 ? (
                  <small style={{ color: 'var(--accent-orange)', width: '100%' }}>
                    Сквады не загружены. Проверьте настройки API в разделе "Сквады".
                  </small>
                ) : (
                  squads.map(squad => {
                    const squadId = squad.uuid || squad.id;
                    const isSelected = selectedSquadIds.includes(squadId);
                    return (
                      <label 
                        key={squadId} 
                        style={{ 
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          userSelect: 'none',
                          border: isSelected ? '2px solid var(--primary)' : '2px solid var(--glass-border)',
                          backgroundColor: isSelected ? 'var(--primary)' : 'var(--bg-primary)',
                          color: isSelected ? 'white' : 'var(--text-primary)',
                          transition: 'all 0.2s ease',
                          fontSize: '13px',
                          fontWeight: isSelected ? '600' : '400'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = 'var(--primary)';
                            e.currentTarget.style.backgroundColor = 'var(--surface-muted)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = 'var(--glass-border)';
                            e.currentTarget.style.backgroundColor = 'var(--bg-primary)';
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedSquadIds([...selectedSquadIds, squadId]);
                            } else {
                              setSelectedSquadIds(selectedSquadIds.filter(id => id !== squadId));
                            }
                          }}
                          style={{ 
                            marginRight: '6px',
                            cursor: 'pointer',
                            width: '16px',
                            height: '16px'
                          }}
                        />
                        {squad.name || squad.title || squadId}
                      </label>
                    );
                  })
                )}
              </div>
              {selectedSquadIds.length > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '6px', 
                  marginTop: '10px',
                  padding: '8px',
                  backgroundColor: 'var(--surface-muted)',
                  borderRadius: '6px'
                }}>
                  <small style={{ color: 'var(--text-secondary)', width: '100%', marginBottom: '4px' }}>
                    Выбрано сквадов: <strong style={{ color: 'var(--primary)' }}>{selectedSquadIds.length}</strong>
                  </small>
                  {selectedSquadIds.map((squadId, idx) => {
                    const squad = squads.find(s => (s.uuid || s.id) === squadId);
                    return (
                      <span
                        key={idx}
                        style={{
                          display: 'inline-block',
                          padding: '4px 10px',
                          borderRadius: '4px',
                          backgroundColor: 'var(--primary)',
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}
                      >
                        {squad ? (squad.name || squad.title) : squadId.substring(0, 8) + '...'}
                      </span>
                    );
                  })}
                </div>
              )}
              {selectedSquadIds.length === 0 && (
                <small style={{ display: 'block', marginTop: '8px', color: 'var(--text-secondary)' }}>
                  Если не выбрано, используется дефолтный сквад
                </small>
              )}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group" style={{ flex: '1 1 100%' }}>
              <label>Лимит трафика (в гигабайтах)</label>
              <input 
                type="number" 
                value={trafficLimitGB} 
                onChange={e => setTrafficLimitGB(e.target.value || 0)} 
                min="0"
                step="0.1"
                placeholder="0 = безлимит"
              />
              <small>
                Укажите лимит трафика в гигабайтах. 0 = безлимит. 
                Примеры: 1 (1 GB), 10 (10 GB), 50 (50 GB)
              </small>
            </div>
            <div className="form-group" style={{ flex: '1 1 100%' }}>
              <label>Лимит устройств</label>
              <input 
                type="number" 
                value={hwidDeviceLimit} 
                onChange={e => setHwidDeviceLimit(e.target.value || 0)} 
                min="0"
                step="1"
                placeholder="0 = безлимит"
              />
              <small>
                Укажите максимальное количество устройств. 0 = безлимит.
                Примеры: 1, 3, 5 устройств
              </small>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group" style={{ flex: '1 1 100%' }}>
              <label>Уровень тарифа (опционально)</label>
              <select 
                value={selectedTier} 
                onChange={e => setSelectedTier(e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="">Не выбран</option>
                {tariffLevels.map((level) => (
                  <option key={level.id} value={level.code}>
                    {level.name} ({level.code})
                  </option>
                ))}
              </select>
              <small>
                Если не выбран, уровень будет пустым. Рекомендуется выбирать уровень из списка.
              </small>
            </div>
            <div className="form-group" style={{ flex: '1 1 100%' }}>
              <label>Бейдж (опционально)</label>
              <select 
                value={badge} 
                onChange={e => setBadge(e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="">Без бейджа</option>
                <option value="top_sale">ТОП продаж</option>
              </select>
              <small>Выберите бейдж для отображения на тарифе</small>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group" style={{ flex: '1 1 100%' }}>
              <label>Бонусные дни (опционально)</label>
              <input 
                type="number" 
                value={bonusDays} 
                onChange={e => setBonusDays(parseInt(e.target.value) || 0)} 
                min="0"
                step="1"
                placeholder="0 = без бонуса"
              />
              <small>
                Укажите количество бонусных дней. 0 = без бонуса.
                Отображается как "Бонус +X дней" на карточке тарифа.
              </small>
            </div>
          </div>
          <button type="submit" className="btn" style={{maxWidth: '200px'}}>Создать тариф</button>
        </form>
      </section>
      
      {/* Секция 2: Список тарифов */}
      <section>
        <h2>Текущие тарифы</h2>
        {loading && <div className="loading-mini">Загрузка...</div>}
        {error && <div className="message-error">{error}</div>}
        
        {!loading && !error && (
          <div className="table-wrapper">
            <table className="users-table">
              {/* ... (вся таблица <thead> и <tbody> без изменений) ... */}
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Название</th>
                  <th>Дни</th>
                  <th>UAH</th>
                  <th>RUB</th>
                  <th>USD</th>
                  <th>Сквад</th>
                  <th>Лимит трафика</th>
                  <th>Лимит устройств</th>
                  <th>Уровень</th>
                  <th>Бейдж</th>
                  <th>Бонус</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {tariffs.map(t => {
                  // Получаем все сквады для тарифа (поддержка squad_ids и squad_id для обратной совместимости)
                  let tariffSquadIds = [];
                  if (t.squad_ids) {
                    try {
                      tariffSquadIds = typeof t.squad_ids === 'string' ? JSON.parse(t.squad_ids) : t.squad_ids;
                      if (!Array.isArray(tariffSquadIds)) tariffSquadIds = [];
                    } catch {
                      tariffSquadIds = [];
                    }
                  } else if (t.squad_id) {
                    tariffSquadIds = [t.squad_id];
                  }
                  
                  return (
                    <tr key={t.id}>
                      <td>{t.id}</td>
                      <td>{t.name}</td>
                      <td>{t.duration_days}</td>
                      <td>{t.price_uah}</td>
                      <td>{t.price_rub}</td>
                      <td>{t.price_usd}</td>
                      <td>
                        {tariffSquadIds.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {tariffSquadIds.map((squadId, idx) => {
                              const squad = squads.find(s => (s.uuid || s.id) === squadId);
                              return (
                                <span 
                                  key={idx}
                                  className="code-cell" 
                                  title={squad ? squad.name || squad.title : squadId}
                                  style={{
                                    display: 'inline-block',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    margin: '2px 0'
                                  }}
                                >
                                  {squad ? (squad.name || squad.title) : squadId.substring(0, 8) + '...'}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)' }}>Дефолтный</span>
                        )}
                      </td>
                      <td>
                        {t.traffic_limit_bytes && t.traffic_limit_bytes > 0 ? (
                          <span title={`${t.traffic_limit_bytes} байт`}>
                            {(t.traffic_limit_bytes / 1073741824).toFixed(1)} GB
                          </span>
                        ) : (
                          <span style={{ color: 'var(--accent-primary)' }}>Безлимит</span>
                        )}
                      </td>
                      <td>
                        {t.hwid_device_limit && t.hwid_device_limit > 0 ? (
                          <span>{t.hwid_device_limit}</span>
                        ) : (
                          <span style={{ color: 'var(--accent-primary)' }}>Безлимит</span>
                        )}
                      </td>
                      <td>
                        {t.tier ? (
                          <span className={`role-badge role-${t.tier}`} style={{ textTransform: 'capitalize' }}>
                            {getTierLabel(t.tier)}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>—</span>
                        )}
                      </td>
                      <td>
                        {t.badge ? (
                          <span className="role-badge" style={{ background: 'var(--accent-yellow)', color: '#1f2933', textTransform: 'uppercase', fontSize: '11px' }}>
                            {t.badge === 'top_sale' ? 'ТОП продаж' : t.badge}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>—</span>
                        )}
                      </td>
                      <td>
                        {Number(t.bonus_days) > 0 ? (
                          <span style={{ color: 'var(--accent-primary)', fontWeight: '600' }}>+{t.bonus_days} дней</span>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>—</span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <button 
                            className="btn-table-edit"
                            onClick={() => handleEditTariff(t)}
                            style={{ padding: '6px 12px', fontSize: '13px' }}
                          >
                            Редактировать
                          </button>
                          <button 
                            className="btn-table-delete"
                            onClick={() => handleDeleteTariff(t.id)}
                          >
                            Удалить
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      
      {/* Модальное окно редактирования */}
      {editingTariff && (
        <div className="modal-overlay" onClick={handleCloseEdit}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Редактировать тариф</h2>
              <button className="modal-close" onClick={handleCloseEdit}>×</button>
            </div>
            <form className="tariff-form" onSubmit={handleUpdateTariff}>
              <div className="form-row">
                <div className="form-group">
                  <label>Название (н.п. "1 Месяц")</label>
                  <input 
                    type="text" 
                    value={editName} 
                    onChange={e => setEditName(e.target.value)} 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label>Длительность (в днях)</label>
                  <input 
                    type="number" 
                    value={editDays} 
                    onChange={e => setEditDays(e.target.value)} 
                    required 
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Цена (UAH)</label>
                  <input 
                    type="number" 
                    value={editPriceUAH} 
                    onChange={e => setEditPriceUAH(e.target.value)} 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label>Цена (RUB)</label>
                  <input 
                    type="number" 
                    value={editPriceRUB} 
                    onChange={e => setEditPriceRUB(e.target.value)} 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label>Цена (USD)</label>
                  <input 
                    type="number" 
                    value={editPriceUSD} 
                    onChange={e => setEditPriceUSD(e.target.value)} 
                    required 
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{ flex: '1 1 100%' }}>
                  <label>Сквады (можно выбрать несколько)</label>
                  <div style={{ 
                    border: '1px solid var(--border-color)', 
                    borderRadius: '4px', 
                    padding: '8px', 
                    maxHeight: '200px', 
                    overflowY: 'auto',
                    backgroundColor: 'var(--bg-primary)'
                  }}>
                    {squadsLoading ? (
                      <small>Загрузка сквадов...</small>
                    ) : squads.length === 0 ? (
                      <small style={{ color: 'var(--accent-orange)' }}>
                        Сквады не загружены. Проверьте настройки API в разделе "Сквады".
                      </small>
                    ) : (
                      squads.map(squad => {
                        const squadId = squad.uuid || squad.id;
                        const isSelected = editSelectedSquadIds.includes(squadId);
                        return (
                          <label key={squadId} style={{ 
                            display: 'block', 
                            padding: '6px 0',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setEditSelectedSquadIds([...editSelectedSquadIds, squadId]);
                                } else {
                                  setEditSelectedSquadIds(editSelectedSquadIds.filter(id => id !== squadId));
                                }
                              }}
                              style={{ marginRight: '8px' }}
                            />
                            {squad.name || squad.title || squadId}
                          </label>
                        );
                      })
                    )}
                  </div>
                  {editSelectedSquadIds.length > 0 && (
                    <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-secondary)' }}>
                      Выбрано сквадов: {editSelectedSquadIds.length}
                    </small>
                  )}
                  {editSelectedSquadIds.length === 0 && (
                    <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-secondary)' }}>
                      Если не выбрано, используется дефолтный сквад
                    </small>
                  )}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{ flex: '1 1 100%' }}>
                  <label>Лимит трафика (в гигабайтах)</label>
                  <input 
                    type="number" 
                    value={editTrafficLimitGB} 
                    onChange={e => setEditTrafficLimitGB(e.target.value || 0)} 
                    min="0"
                    step="0.1"
                    placeholder="0 = безлимит"
                  />
                  <small>
                    Укажите лимит трафика в гигабайтах. 0 = безлимит. 
                    Примеры: 1 (1 GB), 10 (10 GB), 50 (50 GB)
                  </small>
                </div>
                <div className="form-group" style={{ flex: '1 1 100%' }}>
                  <label>Лимит устройств</label>
                  <input 
                    type="number" 
                    value={editHwidDeviceLimit} 
                    onChange={e => setEditHwidDeviceLimit(e.target.value || 0)} 
                    min="0"
                    step="1"
                    placeholder="0 = безлимит"
                  />
                  <small>
                    Укажите максимальное количество устройств. 0 = безлимит.
                    Примеры: 1, 3, 5 устройств
                  </small>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{ flex: '1 1 100%' }}>
                  <label>Уровень тарифа (опционально)</label>
                  <select 
                    value={editSelectedTier} 
                    onChange={e => setEditSelectedTier(e.target.value)}
                    style={{ width: '100%' }}
                  >
                    <option value="">Не выбран</option>
                    {tariffLevels.map((level) => (
                      <option key={level.id} value={level.code}>
                        {level.name} ({level.code})
                      </option>
                    ))}
                  </select>
                  <small>
                    Если не выбран, уровень будет пустым. Рекомендуется выбирать уровень из списка.
                  </small>
                </div>
                <div className="form-group" style={{ flex: '1 1 100%' }}>
                  <label>Бейдж (опционально)</label>
                  <select 
                    value={editBadge} 
                    onChange={e => setEditBadge(e.target.value)}
                    style={{ width: '100%' }}
                  >
                    <option value="">Без бейджа</option>
                    <option value="top_sale">ТОП продаж</option>
                  </select>
                  <small>Выберите бейдж для отображения на тарифе</small>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{ flex: '1 1 100%' }}>
                  <label>Бонусные дни (опционально)</label>
                  <input 
                    type="number" 
                    value={editBonusDays} 
                    onChange={e => setEditBonusDays(parseInt(e.target.value) || 0)} 
                    min="0"
                    step="1"
                    placeholder="0 = без бонуса"
                  />
                  <small>
                    Укажите количество бонусных дней. 0 = без бонуса.
                    Отображается как "Бонус +X дней" на карточке тарифа.
                  </small>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button type="submit" className="btn">Сохранить изменения</button>
                <button type="button" className="btn btn-secondary" onClick={handleCloseEdit}>Отмена</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}

export default AdminTariffsPage;